import { Button, Checkbox } from '@mui/joy';

import { dequal } from 'dequal';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useToast from '@/components/Toast/useToast';
import {
  useCreateExperimentViewOverridesMutation,
  useExperimentView,
  useUpdateExperimentViewOverridesMutation,
} from '@/hooks/useSwr';
import {
  DatasetSchema,
  ExperimentViewOverridesSchema,
  ExperimentViewOverridesZodSchema,
  RunFacetField,
} from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { Pane } from '../../../../components/Pane';
import { getColumnStorageName } from '../../utils/columnTypeUtils';
import { getRenderedColumnNames } from '../../utils/renderOverriddenColumns';
import { ExperimentViewEditForm } from './ExperimentViewEditForm';
import {
  ExperimentViewRow,
  addMissingDefaultColumnsToExperimentView,
  convertExperimentViewToRows,
} from './utils/columns';

export interface ExperimentViewConfigPaneProps {
  dataset: DatasetSchema;
  open: boolean;
  aggregateFeedbackKeys: string[];
  facets?: RunFacetField[];
  noAttachments?: boolean;
  onClose: () => void;
  onSuccess?: (experimentView?: ExperimentViewOverridesSchema) => void;
}

export function ExperimentViewConfigPane({
  dataset,
  aggregateFeedbackKeys,
  noAttachments,
  open,
  facets,
  onClose,
  onSuccess,
}: ExperimentViewConfigPaneProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});

  const handleValidationChange = (rowId: string, errors: string[]) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (errors.length === 0) {
        delete newErrors[rowId];
      } else {
        newErrors[rowId] = errors;
      }
      return newErrors;
    });
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const { createToast } = useToast();
  const localExperimentViewOverrideKey =
    `ls:localExperimentViewOverride-${dataset.id}` as const;
  const [localExperimentViewOverride, setLocalExperimentViewOverride] =
    useLocalStorageState<ExperimentViewOverridesSchema | undefined>(
      localExperimentViewOverrideKey,
      undefined
    );

  // Assigning the current edit session an id allows child components to
  // unmount and rerender if the user resets the form
  const [formSessionId, setFormSessionId] = useState(uuidv4());

  const [saveToServer, setSaveToServer] = useState(false);

  const { data: serverExperimentView, isLoading: experimentViewIsLoading } =
    useExperimentView(dataset.id, {
      revalidateOnFocus: false,
    });

  const defaultColumnNames = getRenderedColumnNames(
    [],
    {
      isReferenceInputHidden: false,
      isAttachmentsHidden: noAttachments ?? false,
      isReferenceOutputHidden: false,
      hiddenFeedbackColumns: [],
      hiddenMetricsColumns: [],
    },
    aggregateFeedbackKeys,
    true
  );

  const defaultExperimentViewOverride: ExperimentViewOverridesSchema =
    serverExperimentView ?? {
      id: '',
      dataset_id: dataset.id,
      column_overrides: defaultColumnNames.map((column) => ({
        column: column,
        hide: false,
        precision: 2,
      })),
    };

  const { trigger: createExperimentViewOverrides } =
    useCreateExperimentViewOverridesMutation(dataset.id);
  const { trigger: updateExperimentViewOverrides } =
    useUpdateExperimentViewOverridesMutation(
      serverExperimentView?.id,
      dataset.id
    );

  const [experimentView, setExperimentView] = useState(
    saveToServer
      ? defaultExperimentViewOverride
      : localExperimentViewOverride ?? defaultExperimentViewOverride
  );

  const [updatedExperimentView, setUpdatedExperimentView] = useState(
    addMissingDefaultColumnsToExperimentView(experimentView, defaultColumnNames)
  );

  // An extra level of state for tracking table rows
  // sadly seems necessary here to avoid race conditions in
  // drag and drop table rendering.
  const [rows, setRows] = useState<ExperimentViewRow[]>([]);

  useEffect(() => {
    const experimentViewWithMissingColumns =
      addMissingDefaultColumnsToExperimentView(
        experimentView,
        defaultColumnNames
      );
    setUpdatedExperimentView(experimentViewWithMissingColumns);
    setRows(
      convertExperimentViewToRows(
        experimentViewWithMissingColumns,
        aggregateFeedbackKeys,
        noAttachments
      )
    );
  }, [experimentView]);

  const resetFormStates = useCallback(
    (currentView?: ExperimentViewOverridesSchema) => {
      const experimentViewWithMissingColumns =
        addMissingDefaultColumnsToExperimentView(
          currentView ?? defaultExperimentViewOverride,
          defaultColumnNames
        );
      setUpdatedExperimentView(experimentViewWithMissingColumns);
      setRows(
        convertExperimentViewToRows(
          experimentViewWithMissingColumns,
          aggregateFeedbackKeys,
          noAttachments
        )
      );
      setSaveToServer(false);
      setFormSessionId(uuidv4());
    },
    [experimentView]
  );

  const closeAndReset = useCallback(
    (currentView?: ExperimentViewOverridesSchema) => {
      onClose();
      resetFormStates(currentView);
    },
    [onClose, resetFormStates]
  );

  const convertRowsToExperimentView = useCallback(
    (currentRows: ExperimentViewRow[]): ExperimentViewOverridesSchema => {
      return {
        ...experimentView,
        id: experimentView?.id ?? '',
        dataset_id: experimentView?.dataset_id ?? '',
        column_overrides: currentRows.map((row) => ({
          column: getColumnStorageName(row.values.name || ''),
          hide: row.values.hide || false,
          precision: row.values.precision ?? 2,
          color_gradient: row.values.color_gradient,
        })),
      };
    },
    [experimentView]
  );

  const handleExperimentViewChange = useCallback(
    (newRows: ExperimentViewRow[]) => {
      setRows(newRows);
      setUpdatedExperimentView((current) => {
        const newExperimentView = convertRowsToExperimentView(newRows);
        // Only update if the data has actually changed
        if (dequal(current, newExperimentView)) {
          return current;
        }
        return newExperimentView;
      });
    },
    []
  );

  const handleSubmit = async () => {
    // Check for duplicate column overrides
    const columnOverrides = updatedExperimentView?.column_overrides ?? [];
    const columnNames = columnOverrides.map((override) => override.column);
    const emptyColumnNames = columnNames.filter((columnName) => !columnName);
    if (emptyColumnNames.length > 0) {
      createToast({
        title: 'Error: Empty column configuration',
        description: `Please remove any blank rows.`,
        error: true,
      });
      return;
    }

    const duplicateColumnNames = columnNames.filter(
      (id, index) => columnNames.indexOf(id) !== index
    );

    if (duplicateColumnNames.length > 0) {
      createToast({
        title: 'Error: Duplicate column configs',
        description: `Please remove duplicate entries for: ${duplicateColumnNames.join(
          ', '
        )}`,
        error: true,
      });
      return;
    }

    try {
      ExperimentViewOverridesZodSchema.parse(updatedExperimentView);
    } catch (error: any) {
      if (Array.isArray(error.issues)) {
        const friendlyError = error.issues.map((err) => err.message).join('\n');
        createToast({
          title: 'Validation error',
          description: friendlyError,
          error: true,
        });
      } else {
        createToast({
          title: 'Validation error',
          description: error.message,
          error: true,
        });
      }
      return;
    }

    setIsSubmitting(true);
    try {
      if (saveToServer) {
        const result = serverExperimentView
          ? await updateExperimentViewOverrides({
              json: {
                column_overrides: updatedExperimentView?.column_overrides ?? [],
              },
            })
          : await createExperimentViewOverrides({
              json: {
                column_overrides: updatedExperimentView?.column_overrides ?? [],
              },
            });
        createToast({
          title: '',
          description: 'Default configuration settings saved successfully',
        });
        setLocalExperimentViewOverride(undefined);
        // Directly clear from localStorage to avoid race condition
        window.localStorage.removeItem(localExperimentViewOverrideKey);
        onSuccess?.(result);
      } else {
        // Save to localStorage only
        // If the local override is the same as server's, remove from localStorage
        if (
          dequal(
            updatedExperimentView?.column_overrides ?? [],
            defaultExperimentViewOverride?.column_overrides ?? []
          )
        ) {
          setLocalExperimentViewOverride(undefined);
          createToast({
            title: '',
            description: 'Configuration settings set to dataset default',
          });
        } else {
          setLocalExperimentViewOverride(updatedExperimentView);
          createToast({
            title: '',
            description: 'Configuration settings saved successfully',
          });
        }
        onSuccess?.(updatedExperimentView);
      }
      setExperimentView(updatedExperimentView);
      closeAndReset(updatedExperimentView);
    } catch (error) {
      createToast({
        title: 'Failed to update experiment view configuration.',
        description: (error as Error).message,
        error: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitButton = (
    <Button
      variant="solid"
      color="primary"
      loading={isSubmitting}
      disabled={hasValidationErrors}
      onClick={handleSubmit}
    >
      Save
    </Button>
  );

  const cancelButton = (
    <Button
      onClick={() => {
        closeAndReset(experimentView);
      }}
      color="neutral"
      variant="outlined"
      disabled={isSubmitting}
    >
      Cancel
    </Button>
  );

  return (
    <Pane
      open={open}
      onClose={() => {
        closeAndReset(experimentView);
      }}
      title="Experiment View Configuration"
      className="flex h-[500px] flex-col p-0"
      dialogStyle={{
        marginLeft: 'calc(max(4.5rem, 100vw - max(20vw, 900px)))',
      }}
    >
      <div className="h-full overflow-y-auto px-8 py-6">
        <ExperimentViewEditForm
          dataset={dataset}
          rows={rows}
          formSessionId={formSessionId}
          noAttachments={noAttachments}
          facets={facets}
          onChange={handleExperimentViewChange}
          onValidationChange={handleValidationChange}
          isLoading={experimentViewIsLoading}
          onReset={() => {
            resetFormStates(defaultExperimentViewOverride);
          }}
        />
      </div>
      <div className="mt-auto flex w-full flex-col border-t border-primary px-8 py-4">
        <div className="flex items-center gap-2">
          <label className="flex w-full items-center py-4">
            <div className="flex">
              <Checkbox
                checked={saveToServer}
                onChange={(e) => setSaveToServer(e.target.checked)}
                className="mt-1"
              />
              <div className="ml-2 flex flex-col">
                <div className="text-l">
                  Apply to all experiments in{' '}
                  <span className="font-semibold">{dataset.name}</span>
                </div>
                <div className="text-sm text-sm text-quaternary">
                  This will be the default view for anyone viewing this dataset.
                </div>
              </div>
            </div>
          </label>
          <div className="flex items-center gap-2">
            {cancelButton}
            {submitButton}
          </div>
        </div>
      </div>
    </Pane>
  );
}
